// Whiteboard — one open board's link to the server and to everyone else on it.
//   SAVING: every change waits in the board model's `pending`; a short pause
//   (or a second at most while drawing on) sends it — only the elements that
//   changed. A failed save keeps them and tries again, later and later; the
//   status says so ("Not saved — trying again").
//   LIVE: the Socket.IO room of this board. Others' saved elements arrive
//   as `whiteboard:elements`; their cursors, selections, strokes still being
//   drawn and laser trails as `whiteboard:presence` (never stored).

import { socket } from '@platform/http/socket';
import whiteboardApi from '@modules/database/whiteboard/frontend/api';
import { newId } from '@modules/database/whiteboard/frontend/engine/scene';

const QUIET_MS = 300;
const MAX_WAIT_MS = 1200;
const CHUNK_BYTES = 1_500_000;     // under the server's 5 MB JSON limit, with room
const PRESENCE_MS = 50;
const PEER_TIMEOUT_MS = 60_000;

export function createSession({ model, files, key, fieldId, room, canEdit }) {
  const clientId = newId(12);
  const listeners = new Set();
  const state = { status: canEdit ? 'saved' : 'readonly', error: null, peers: new Map(), me: null, connected: socket.connected, rev: 0 };
  const emit = () => { state.rev += 1; for (const fn of listeners) fn(); };
  let timer = null; let firstPendingAt = 0; let inflight = false; let failures = 0; let stopped = false;
  let retryTimer = null;

  function setStatus(s, error = null) {
    if (state.status === s && state.error === error) return;
    state.status = s; state.error = error; emit();
  }

  // ---- saving ------------------------------------------------------------------------
  function schedule(now = false) {
    if (!canEdit || stopped) return;
    if (!model.hasPending()) return;
    if (!firstPendingAt) firstPendingAt = Date.now();
    clearTimeout(timer);
    const wait = now ? 0 : Math.max(0, Math.min(QUIET_MS, MAX_WAIT_MS - (Date.now() - firstPendingAt)));
    timer = setTimeout(flush, wait);
    if (state.status === 'saved') setStatus('saving');
  }

  function chunks(list) {
    const out = []; let cur = []; let size = 0;
    for (const el of list) {
      const n = JSON.stringify(el).length;
      if (cur.length && (size + n > CHUNK_BYTES || cur.length >= 1500)) { out.push(cur); cur = []; size = 0; }
      cur.push(el); size += n;
    }
    if (cur.length) out.push(cur);
    return out;
  }

  async function flush() {
    if (inflight || stopped && !model.hasPending()) return;
    const batch = model.takePending((el) => files.ready(el) && !files.failed(el));
    if (!batch.length) {
      firstPendingAt = 0;
      setStatus(model.hasPending() ? 'uploading' : 'saved');
      return;
    }
    inflight = true; firstPendingAt = 0;
    setStatus('saving');
    const parts = chunks(batch);
    let sent = 0;
    try {
      for (const part of parts) {
        const res = await whiteboardApi.save(key, fieldId, part, clientId);
        sent += part.length;
        model.settle(part);
        if (res?.newer?.length) model.applyRemote(res.newer);
      }
      failures = 0;
      setStatus(model.hasPending() ? 'saving' : 'saved');
    } catch (e) {
      model.restorePending(batch.slice(sent));
      const code = e?.response?.status;
      const msg = e?.response?.data?.error || e?.message || 'The whiteboard could not be saved.';
      if (code === 403) { setStatus('readonly', msg); return; }
      if (code === 400 || code === 409 || code === 413) {
        // the server will never take these: say so, and stop retrying them
        setStatus('error', msg);
        model.settle(model.takePending((el) => batch.some((b) => b.id === el.id)));
        return;
      }
      failures += 1;
      setStatus('offline', msg);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => schedule(true), Math.min(30000, 1000 * 2 ** Math.min(5, failures)));
    } finally {
      inflight = false;
      if (!stopped && model.hasPending() && state.status !== 'offline' && state.status !== 'readonly') schedule();
    }
  }

  const unsubModel = model.subscribe(() => { if (model.hasPending()) schedule(); });
  const unsubFiles = files.subscribe(() => { if (model.hasPending()) schedule(); });

  // ---- live ----------------------------------------------------------------------------
  function onElements(msg) {
    if (msg?.room !== room || msg.clientId === clientId) return;
    model.applyRemote(msg.elements || []);
    for (const el of msg.elements || []) if (el.type === 'image') files.ensure(el.fileId);
  }
  function onPeer(msg) {
    if (msg?.room !== room) return;
    const cur = state.peers.get(msg.sid) || {};
    state.peers.set(msg.sid, { ...cur, sid: msg.sid, accountId: msg.accountId, name: msg.name, color: msg.color, at: Date.now() });
    emit();
  }
  function onPeerLeft(msg) {
    if (msg?.room !== room) return;
    if (state.peers.delete(msg.sid)) emit();
  }
  function onPresence(msg) {
    if (msg?.room !== room) return;
    const cur = state.peers.get(msg.sid) || {};
    const next = { ...cur, sid: msg.sid, accountId: msg.accountId, name: msg.name, color: msg.color, at: Date.now() };
    for (const k of ['x', 'y', 'tool', 'sel', 'drafts', 'laser', 'erasing', 'away']) next[k] = msg[k];
    state.peers.set(msg.sid, next);
    emit();
  }
  function join() {
    socket.emit('whiteboard:join', { key, fieldId }, (reply) => {
      if (!reply?.ok) return;
      state.me = reply.you || null;
      state.peers = new Map((reply.peers || []).map((p) => [p.sid, { ...p, at: Date.now() }]));
      emit();
    });
  }
  async function onConnect() {
    state.connected = true; emit();
    join();
    // back from a drop: read the board again (my unsaved changes stay on top)
    try {
      const fresh = await whiteboardApi.board(key, fieldId);
      model.load(fresh.elements || []);
      for (const el of fresh.elements || []) if (el.type === 'image') files.ensure(el.fileId);
    } catch { /* the next save says how it is */ }
    if (model.hasPending()) schedule(true);
  }
  function onDisconnect() { state.connected = false; state.peers = new Map(); emit(); }

  socket.on('whiteboard:elements', onElements);
  socket.on('whiteboard:peer', onPeer);
  socket.on('whiteboard:peer-left', onPeerLeft);
  socket.on('whiteboard:presence', onPresence);
  let firstConnect = true;
  const handleConnect = () => { if (firstConnect) { firstConnect = false; state.connected = true; emit(); join(); return; } onConnect(); };
  socket.on('connect', handleConnect);
  socket.on('disconnect', onDisconnect);
  if (socket.connected) handleConnect();
  else {
    try { if (localStorage.getItem('token')) socket.connect(); } catch { /* storage may be blocked */ }
  }

  // forget people who went quiet without saying goodbye
  const sweeper = setInterval(() => {
    const cutoff = Date.now() - PEER_TIMEOUT_MS; let changed = false;
    for (const [sid, p] of state.peers) if (p.at < cutoff) { state.peers.delete(sid); changed = true; }
    if (changed) emit();
  }, 15000);

  // presence: the latest wins, at most every PRESENCE_MS
  let presTimer = null; let presNext = null; let presLast = 0;
  function sendPresence(p) {
    presNext = { ...(presNext || {}), ...p, room };
    if (presTimer) return;
    const wait = Math.max(0, PRESENCE_MS - (Date.now() - presLast));
    presTimer = setTimeout(() => {
      presTimer = null; presLast = Date.now();
      const out = presNext; presNext = null;
      if (socket.connected && out) socket.emit('whiteboard:presence', out);
    }, wait);
  }

  const beforeUnload = (e) => {
    if (canEdit && model.hasPending()) { flush(); e.preventDefault(); e.returnValue = ''; }
  };
  window.addEventListener('beforeunload', beforeUnload);

  return {
    clientId,
    state,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    sendPresence,
    saveNow: () => schedule(true),
    stop() {
      stopped = true;
      clearTimeout(timer); clearTimeout(retryTimer); clearTimeout(presTimer); clearInterval(sweeper);
      unsubModel(); unsubFiles();
      window.removeEventListener('beforeunload', beforeUnload);
      socket.off('whiteboard:elements', onElements);
      socket.off('whiteboard:peer', onPeer);
      socket.off('whiteboard:peer-left', onPeerLeft);
      socket.off('whiteboard:presence', onPresence);
      socket.off('connect', handleConnect);
      socket.off('disconnect', onDisconnect);
      if (socket.connected) socket.emit('whiteboard:leave', { room });
      // the last changes still go (the request outlives the screen)
      if (canEdit && model.hasPending()) { stopped = false; flush(); stopped = true; }
    },
  };
}
